// ===========================
// Premium Sidebar.jsx
// ===========================

import React, { useState, useRef, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import * as FaIcons from "react-icons/fa";
import * as HiIcons from "react-icons/hi";
import * as BiIcons from "react-icons/bi";
import { SidebarData } from "./SidebarData";
import { IconContext } from "react-icons";
import { userContext } from "../../App";
import { Form, FormControl, Button } from "react-bootstrap";

// ===========================
// Premium Colors
// ===========================
const COLORS = {
  sidebarBg: "#0f172a",
  sidebarText: "#f1f5f9",
  accent: "#22d3ee",
  accentHover: "rgba(34,211,238,0.12)",
  active: "#06b6d4",
  brandGradient: "linear-gradient(90deg,#22d3ee,#3b82f6)",
};

// ===========================
// NAV BAR
// ===========================
const Nav = styled.div`
  background: ${COLORS.sidebarBg};
  height: 55px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 2px;
  z-index: 20;
  position: fixed;
  top: 0;
  left: 0;

  /* LEFT aligned */
  justify-content: flex-start;
`;
const NavIconOpen = styled.div`
  font-size: 28px;
  cursor: pointer;
  color: ${COLORS.accent};
  display: flex;
  align-items: center;
  margin-right: 12px;

  &:hover {
    opacity: 0.8;
  }
`;

const NavIconClose = styled(NavIconOpen)`
  transform: rotate(90deg);
`;


const Brand = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.1;
  cursor: pointer;
  margin-left: 0px;

  span:first-child {
    color: white;
    font-weight: 700;
    font-size: 20px;
  }

  span:last-child {
    color: #fb641b;
    font-weight: 800;
    font-size: 20px;
  }

  /* Desktop: single line */
  @media (min-width: 769px) {
    flex-direction: row;
    gap: 6px;

    span {
      font-size: 20px;
    }
  }

  /* Mobile: two lines */
  @media (max-width: 768px) {
    span {
      font-size: 16px;
    }
    span:first-child {
        color: white;
        font-weight: 700;
        font-size: 12px;
    }

    span:last-child {
        color: #fb641b;
        font-weight: 800;
        font-size: 16px;
    }
  }
`;

const SearchForm = styled(Form)`
  margin-left: auto; /* pushes search to right */
  display: flex;
  align-items: center;
  gap: 6px;

  /* 🔥 Make search visible on mobile */
  @media (max-width: 768px) {
    display: flex;
  }

  input {
    height: 32px;
    font-size: 14px;
  }

  button {
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;


// ===========================
// SIDEBAR
// ===========================
const SidebarNav = styled.nav`
  background: ${COLORS.sidebarBg};
  width: 260px;
  height: 100vh;
  position: fixed;
  top: 55px;
  left: ${({ $open }) => ($open ? "0" : "-260px")};
  transition: left 280ms ease;
  padding: 0;
  z-index: 9999;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    top: 55px;
  }
`;

const SidebarWrap = styled.nav`
  width: 100%;
  text-align: left;          /* <-- forces left alignment */
  display: flex;
  flex-direction: column;
  align-items: flex-start;   /* <-- icons + text left */
  padding-left: 10px;
`;


// ===========================
// SIDEBAR ITEM
// ===========================
const MenuItem = styled.div`
  color: ${COLORS.sidebarText};
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 8px;
  transition: 180ms;
  user-select: none;

  background: ${({ $active }) =>
    $active ? COLORS.accentHover : "transparent"};

  border-left: ${({ $active }) =>
    $active ? `3px solid ${COLORS.accent}` : "3px solid transparent"};

  &:hover {
    background: ${COLORS.accentHover};
  }
`;

const MenuText = styled.span`
  flex: 1;
  line-height: 1.3; /* fixes multi-line alignment */
`;

const SubmenuWrapper = styled.div`
  padding-left: 34px;
  
`;

const SubmenuItem = styled.div`
  padding: 10px 10px;
  font-size: 14px;
  text-align: left;
  justify-content: flex-start;
  align-items: center;
  display: flex;
  gap: 10px;
  cursor: pointer;
  color: ${COLORS.sidebarText};
  border-radius: 6px;
  transition: 180ms;

  &:hover {
    background: ${COLORS.accentHover};
    color: ${COLORS.active};
  }
`;

const NotificationIcon = styled.nav`
    margin-top: 5px;
    margin-right: 8px;
    position: relative;
`;

const NotificationText = styled.nav`
    position: fixed;
    height: 20px;
    width: 20px;
    margin-left: 15px;
    font-size:13px;
    border-radius: 50%;
    background: #f42;
    color: #fff;
`;


// ===========================
// SUBMENU COMPONENT
// ===========================
const Submenu = ({ item }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const active =
    item.path && location.pathname === item.path;

  const handleMainClick = () => {
    // If it has submenu → toggle
    if (item.subNav) {
      setOpen(!open);
      return;
    }

    // If external link
    if (item.external) {
      window.open(item.path, "_blank");
      return;
    }

    // Normal navigation
    if (item.path) {
      window.location.href = item.path;
    }
  };

  return (
    <>
      <MenuItem
        onClick={handleMainClick}
        $active={active}
      >
        {item.icon}
        <MenuText>{item.title}</MenuText>

        {/* Only show arrows if submenu exists */}
        {item.subNav &&
          (open ? item.iconOpened : item.iconClosed)}
      </MenuItem>

      {open && item.subNav && (
        <SubmenuWrapper>
          {item.subNav.map((sub, i) => {
            const isActive = location.pathname === sub.path;

            const handleClick = () => {
              if (sub.external) {
                window.open(sub.path, "_blank");
              } else {
                window.location.href = sub.path;
              }
            };

            return (
              <SubmenuItem key={i} onClick={handleClick} $active={isActive}>
                {sub.icon}
                {sub.title}
              </SubmenuItem>
            );
          })}
        </SubmenuWrapper>
      )}
    </>
  );
};


const FinanceIcon = () => {
    const handleIconClick = () => {
      // Open the finance page in the same tab
      window.location.href = "https://shoppingdeals.in/finance";
      
      // Alternatively, to open in a new tab:
      // window.open("https://shoppingdeals.in/finance", "_blank");
    };
  
    return (
      <div 
        onClick={handleIconClick}
        style={{ 
          cursor: 'pointer', 
          display: 'inline-block',
          transition: 'opacity 0.3s ease',
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.2'}
      >
        <FaIcons.FaRegMoneyBillAlt size={30} opacity={1} />
      </div>
    );
  };

const ProfileIcon = () => {
    const handleIconClick = () => {
        window.location.href = "/dashboard";
        // window.location.href = "/login";
    };
  
    return (
      <div 
        onClick={handleIconClick}
        style={{ 
          cursor: 'pointer', 
          display: 'inline-block',
          transition: 'opacity 0.3s ease',
          margin: '1px',
          marginBottom: '12px'
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
      >
        <FaIcons.FaRegUser size={25} opacity={0.8} />
      </div>
    );
  };
// ===========================
// MAIN SIDEBAR COMPONENT
// ===========================
const Sidebar = () => {
  const location = useLocation();
  const { state } = useContext(userContext);
  const [open, setOpen] = useState(false);

  // Auto close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Outside click detection
  const ref = useRef();
  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  return (
    <IconContext.Provider value={{ color: COLORS.sidebarText }}>
      <div ref={ref}>
        <Nav>
          {!open ? (
            <NavIconOpen>
              <FaIcons.FaBars onClick={() => setOpen(true)} />
            </NavIconOpen>
          ) : (
            <NavIconClose>
              <HiIcons.HiOutlineChevronDoubleDown onClick={() => setOpen(false)} />
            </NavIconClose>
          )}

        <Brand onClick={() => (window.location.href = "/")}>
        <span>Shopping</span>
        <span>Deals</span>
        </Brand>

          {/* Existing functionality preserved */}
          <SearchForm>
             <NotificationIcon>
                {/* <FaIcons.FaRegMoneyBillAlt size={30} opacity={1}/> */}
                {/* <FinanceIcon/> */}
            </NotificationIcon>
            {/* <NotificationIcon>
                {(state === 0) ? (
                    <FaIcons.FaBell size={30} opacity={0.4} />
                )
                    : (
                        <>
                            <NotificationText>{state}</NotificationText>
                            <FaIcons.FaBell size={30} onClick={() => window.location.reload()} />
                        </>
                    )
                }
            </NotificationIcon> */}
                           
            <FormControl
              type="text"
              placeholder="Search"
              className="mb-2 me-2"
              name="search"
              id="header-search"
            />
            <Button variant="secondary" className="mb-2">
              <BiIcons.BiSearchAlt2 size={15} />
            </Button>
             <ProfileIcon/>  
          </SearchForm>
        </Nav>

        <SidebarNav $open={open}>
          <SidebarWrap>
            {SidebarData.map((item, i) => (
              <Submenu item={item} key={i} />
            ))}
          </SidebarWrap>
        </SidebarNav>
      </div>
    </IconContext.Provider>
  );
};

export default Sidebar;
